import { type Tranche } from "@lib/tranche";
import Matrix from "algorithm/matrix";

/**
 * Convert an incremental tranche list to a cumulative tranche list.
 */
export function trancheListToAlgoTranches(
  tranches: Tranche[],
  evaluableIndicesMap: { [evaluableId: string]: number },
  evaluableMaxAllocations: { [evaluableId: string]: number | null },
  evaluableIdWhitelist: Set<string>,
): Matrix<"tranche", "evaluable+hold"> {
  const numEvaluables = Object.keys(evaluableIndicesMap).length;
  const result = Matrix.zeros<"tranche", "evaluable+hold">(
    tranches.length,
    numEvaluables,
  );
  if (tranches.length === 0 || evaluableIdWhitelist.size === 0) {
    return result;
  }

  // Build compact active-column metadata once so the row loop is dense.
  const activeColumns: number[] = [];
  const activeMaxAllocations: number[] = [];
  const activeIndexByEvaluableId: { [evaluableId: string]: number } = {};

  for (const evaluableId of evaluableIdWhitelist) {
    const col = evaluableIndicesMap[evaluableId];
    if (col == null) {
      continue;
    }
    activeIndexByEvaluableId[evaluableId] = activeColumns.length;
    activeColumns.push(col);
    activeMaxAllocations.push(evaluableMaxAllocations[evaluableId] ?? Infinity);
  }

  if (activeColumns.length === 0) {
    return result;
  }

  const runningAllocations = new Float64Array(activeColumns.length);
  const data = result.data;

  for (let row = 0; row < tranches.length; row++) {
    const tranche = tranches[row];
    if (tranche != null) {
      for (const evaluableId in tranche.allocations) {
        const activeIndex = activeIndexByEvaluableId[evaluableId];
        if (activeIndex == null) {
          continue;
        }

        const alloc = tranche.allocations[evaluableId];
        if (alloc == null || alloc.amount === 0) {
          continue;
        }

        const maxAlloc = activeMaxAllocations[activeIndex] ?? Infinity;
        const current = runningAllocations[activeIndex] ?? 0;
        const next = current + alloc.amount;
        runningAllocations[activeIndex] = next > maxAlloc ? maxAlloc : next;
      }
    }

    const rowOffset = row * numEvaluables;
    for (let i = 0; i < activeColumns.length; i++) {
      const col = activeColumns[i];
      if (col == null) {
        continue;
      }
      data[rowOffset + col] = runningAllocations[i] ?? 0;
    }
  }

  return result;
}
